import React, { useState } from 'react';
import './Journal.css';

const Journal = () => {
  const [entries, setEntries] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState("What small moment from today brought you unexpected joy?");
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [currentEntry, setCurrentEntry] = useState({
    id: Date.now(),
    type: 'prompted',
    prompt: "What small moment from today brought you unexpected joy?",
    content: '',
    timestamp: new Date().toISOString()
  });

  // Sample prompts - better copy, more resonant
  const prompts = [
    "What small moment from today brought you unexpected joy?",
    "What would you tell yourself from a year ago?",
    "What are you grateful for right now?",
    "What challenge taught you something recently?",
    "What would you do if you couldn't fail?",
    "How would you spend your ideal day?",
    "Who deserves your thanks today?",
    "What feeling have you been avoiding?",
    "What does contentment look like for you?",
    "What would your future self remind you of?"
  ];

  const addPromptedEntry = () => {
    // Remove empty entries and switch to prompted mode
    const savedEntries = entries.filter(entry => entry.content.trim() !== '');
    setEntries(savedEntries);
    
    setCurrentEntry({
      id: Date.now(),
      type: 'prompted',
      prompt: currentPrompt,
      content: '',
      timestamp: new Date().toISOString()
    });
  };

  const addFreeEntry = () => {
    // Remove empty entries and switch to free writing mode
    const savedEntries = entries.filter(entry => entry.content.trim() !== '');
    setEntries(savedEntries);
    
    setCurrentEntry({
      id: Date.now(),
      type: 'free',
      content: '',
      timestamp: new Date().toISOString()
    });
  };

  const updateCurrentEntry = (content) => {
    setCurrentEntry(prev => ({ ...prev, content }));
  };

  const saveCurrentEntry = () => {
    if (currentEntry.content.trim()) {
      setEntries([currentEntry, ...entries]);
      // Only create new empty entry if this one has substantial content
      if (currentEntry.content.trim().length > 10) {
        setCurrentEntry({
          id: Date.now(),
          type: currentEntry.type,
          prompt: currentEntry.type === 'prompted' ? currentPrompt : undefined,
          content: '',
          timestamp: new Date().toISOString()
        });
      }
    }
  };

  const updateEntry = (id, content) => {
    setEntries(entries.map(entry => 
      entry.id === id ? { ...entry, content } : entry
    ));
  };

  const deleteEntry = (id) => {
    setEntries(entries.filter(entry => entry.id !== id));
  };

  const getNewPrompt = () => {
    const availablePrompts = prompts.filter(p => p !== currentPrompt);
    const randomPrompt = availablePrompts[Math.floor(Math.random() * availablePrompts.length)];
    setCurrentPrompt(randomPrompt);
    setIsEditingPrompt(false);
    
    // Update current entry if it's prompted
    if (currentEntry.type === 'prompted') {
      setCurrentEntry(prev => ({ 
        ...prev, 
        prompt: randomPrompt,
        timestamp: new Date().toISOString()
      }));
    }
  };

  const handleEditPrompt = () => {
    setCustomPrompt(currentPrompt);
    setIsEditingPrompt(true);
  };

  const saveCustomPrompt = () => {
    if (customPrompt.trim()) {
      setCurrentPrompt(customPrompt.trim());
      // Update current entry if it's prompted
      if (currentEntry.type === 'prompted') {
        setCurrentEntry(prev => ({ 
          ...prev, 
          prompt: customPrompt.trim(),
          timestamp: new Date().toISOString()
        }));
      }
    }
    setIsEditingPrompt(false);
    setCustomPrompt("");
  };

  const cancelEdit = () => {
    setIsEditingPrompt(false);
    setCustomPrompt("");
  };

  const autoExpand = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 300) + 'px';
  };

  return (
    <div className="journal-container">
      
      <header className="journal-header">
        <h1 className="journal-title">Journal</h1>
        <p className="journal-subtitle">A space for your thoughts</p>
      </header>

      <section className="prompt-section">
        <div className="prompt-header">
          <span className="prompt-label">Today's Prompt</span>
          <div className="prompt-actions">
            <button 
              className="prompt-action-btn" 
              onClick={handleEditPrompt} 
              title="Edit prompt"
            >
              ✎
            </button>
            <button 
              className="prompt-action-btn" 
              onClick={getNewPrompt} 
              title="Get new prompt"
            >
              ↻
            </button>
          </div>
        </div>
        
        {isEditingPrompt ? (
          <div className="prompt-editor">
            <textarea
              className="prompt-textarea"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              placeholder="Write your own prompt..."
            //   autoFocus
              rows="3"
            />
            <div className="prompt-editor-actions">
              <button className="prompt-save-btn" onClick={saveCustomPrompt}>
                Save
              </button>
              <button className="prompt-cancel-btn" onClick={cancelEdit}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="current-prompt" onClick={handleEditPrompt}>
            {currentPrompt}
          </p>
        )}
      </section>

      <div className="action-buttons">
        <button 
          className={`action-button ${currentEntry.type === 'prompted' ? 'active' : ''}`}
          onClick={addPromptedEntry}
        >
          Prompted
        </button>
        <button 
          className={`action-button ${currentEntry.type === 'free' ? 'active' : ''}`}
          onClick={addFreeEntry}
        >
          Free Write
        </button>
      </div>

      {/* Current Active Entry */}
      <div className="current-entry">
        <div className="entry-header">
          <div className="entry-meta">
            <span className="entry-type">
              {currentEntry.type === 'prompted' ? 'Prompted Entry' : 'Free Writing'}
            </span>
            <span className="entry-timestamp">
              {new Date(currentEntry.timestamp).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>
        
        {currentEntry.type === 'prompted' && (
          <div className="entry-prompt">{currentEntry.prompt}</div>
        )}
        
        <textarea
          className="entry-textarea"
          value={currentEntry.content}
          onChange={(e) => {
            updateCurrentEntry(e.target.value);
            autoExpand(e);
          }}
          onInput={autoExpand}
          onBlur={saveCurrentEntry}
          placeholder={
            currentEntry.type === 'prompted' 
              ? "Your thoughts..."
              : "What's on your mind..."
          }
        //   autoFocus
          style={{ height: 'auto', minHeight: '120px' }}
        />
      </div>

      <div className="entries-container">
        {entries.map((entry) => (
          <div key={entry.id} id={`entry-${entry.id}`} className="journal-entry">
            <div className="entry-header">
              <div className="entry-meta">
                <span className="entry-type">
                  {entry.type === 'prompted' ? 'Prompted Entry' : 'Free Writing'}
                </span>
                <span className="entry-timestamp">
                  {new Date(entry.timestamp).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              <button 
                className="delete-button" 
                onClick={() => deleteEntry(entry.id)}
                title="Delete entry"
              >
                ×
              </button>
            </div>
            
            {entry.type === 'prompted' && (
              <div className="entry-prompt">{entry.prompt}</div>
            )}
            
            <div className="entry-content">
              {entry.content}
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default Journal;